import { BarChartOutlined, FileDoneOutlined, FileTextOutlined, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import moment from 'moment';

// Helper to determine if the current user is an Initiative Owner
const useIsInitiativeOwner = () => {
    const currentUser = useSelector(selectAuth);
    return useCallback(() => {
        return currentUser.role.name === 'Initiative Owner' || currentUser.role.name === 'Workstream Lead' || currentUser.role.name === 'Workstream Sponsor';
    }, [currentUser]);
};

const useIsOtherUser = () => {
    const currentUser = useSelector(selectAuth);
    const hasSpecialAccess = currentUser?.hasSpecialAccess === true;
    return useCallback(() => {
        return currentUser.role.name === 'CTO' || currentUser?.role?.name === 'TO Finance' || currentUser.role.name === 'Strategy Analyst';
    }, [currentUser]);
};
export const useFilterActions = (
    setAttributeFilters,
    setActiveFilterType,
    setFilterHistory,
    setSelectedMenu,
    setSelectedDateRange,
    history,
    setAggregatedAttributes,
    colorByAttribute,
    selectedGroup,
    setSelectedInfoMenu,
    setColorByAttribute,
    setSelectedImpactGroup,
    setSelectedGroup,
    workstream,
    setSortField,
    setSortOrder,
    subworkstream,
    setYAxis
) => {
    const isInitiativeOwner = useIsInitiativeOwner();
    const isOtherUser = useIsOtherUser();
    const currentUser = useSelector(selectAuth);
    const applyFilter = useCallback(
        (
            filterKey,
            filterValues,
            activeFilterType,
            menuKey,
            menuLabel,
            menuIcon,
            colorByAttribute,
            selectedGroup,
            selectedInfoMenu,
            sortField,
            sortOrder,
            yAxis,
            aggregationKey,
            dateRange
        ) => {
            // This function returns another function, as `useCallback` usually does when passed to `onClick`
            return () => {
                setActiveFilterType(activeFilterType);
                if (menuKey === 'Table') {
                    setAggregatedAttributes(Array.isArray(aggregationKey) ? aggregationKey : [aggregationKey]);
                } else {
                    setAggregatedAttributes([]);
                }

                // ✅ FIX: Always set the filter values (don't toggle)
                setAttributeFilters((prevFilters) => {
                    return {
                        ...prevFilters,
                        [filterKey]: filterValues, // Always set to filterValues, never clear
                    };
                });

                if (dateRange && dateRange.length > 0) {
                    setSelectedDateRange(dateRange);
                }
                setFilterHistory((prevHistory) => [
                    ...prevHistory,
                    activeFilterType, // Push 'initiative' or 'milestone' specifically
                    'Filter Options',
                ]);
                setColorByAttribute(colorByAttribute);
                // ✅ Corrected sortField and sortOrder logic
                const sortOrder = 'asc';
                if (sortField != null && sortOrder != null) {
                    setSortField(sortField);
                    setSortOrder(!sortOrder);
                }
                setSelectedMenu({
                    key: menuKey,
                    icon: menuIcon,
                    label: menuLabel,
                });
                if (yAxis !== undefined && yAxis !== null && typeof setYAxis === 'function') {
                    setYAxis(yAxis);
                }
                setSelectedInfoMenu(selectedInfoMenu);
                setSelectedImpactGroup(selectedGroup);
                setSelectedGroup(selectedGroup)
                history.push('?section=workbook');
            };
        },
        [setAttributeFilters, setActiveFilterType, setFilterHistory, setSelectedMenu, history, setYAxis]
    );
    const handleFilterMyInitiativesAndLateMilestones = useCallback(() => {
        const commonParams = {
            menuKey: 'Table',
            menuLabel: 'Table View',
            menuIcon: <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            param1: null,
            param2: null,
            param3: null
        };

        // Parameters specific to initiative filter
        const initiativeFilterParams = {
            filterKey: 'initiativeOwner',
            values: [`${currentUser.firstName} ${currentUser.lastName}`],
            activeFilterType: 'initiative',
            filterType: 'initiatives'
        };
        // Parameters specific to milestone filter
        const milestoneFilterParams = {
            filterKey: 'Planning summary (simplified)',
            values: ['Late', 'Late to start'],
            activeFilterType: 'milestone',
            filterType: 'milestones',
            countType: 'totalLateMilestone'
        };
        const milestoneStatusFilterParams = {
            filterKey: 'Milestone Status',
            values: ['Completed', 'Started', 'Not Started'],
            activeFilterType: 'milestone',
            filterType: 'milestones',
            countType: 'totalLateMilestone'
        };

        if (!isInitiativeOwner()) {
            applyFilter(
                milestoneFilterParams.filterKey,
                milestoneFilterParams.values,
                milestoneFilterParams.activeFilterType,
                commonParams.menuKey,
                commonParams.menuLabel,
                commonParams.menuIcon,
                commonParams.colorByAttribute,
                commonParams.selectedGroup,
                milestoneFilterParams.filterType,
                commonParams.param1,
                commonParams.param2,
                commonParams.param3,
                milestoneFilterParams.countType
            )();
        } else {
            applyFilter(
                initiativeFilterParams.filterKey,
                initiativeFilterParams.values,
                initiativeFilterParams.activeFilterType,
                ...Object.values(commonParams),
                initiativeFilterParams.filterType
            )();

            applyFilter(
                milestoneFilterParams.filterKey,
                milestoneFilterParams.values,
                milestoneFilterParams.activeFilterType,
                ...Object.values(commonParams),
                milestoneFilterParams.filterType,
                null,
                null,
                null,
                milestoneFilterParams.countType
            )();
            applyFilter(
                milestoneStatusFilterParams.filterKey,
                milestoneStatusFilterParams.values,
                milestoneStatusFilterParams.activeFilterType,
                ...Object.values(commonParams),
                milestoneStatusFilterParams.filterType,
                null,
                null,
                null,
                milestoneStatusFilterParams.countType
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleFilterMyInitiatives = useCallback(() => {
        const commonParams = [
            'initiative',        // activeFilterType
            'Table',             // menuKey
            'Table View',         // menuLabel
            <TableOutlined />,   // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'initiativeCount'
        ];
        const pastDueKey = 'Planning summary simplified';
        const pastDueValues = ['Past due'];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention']
        if (isInitiativeOwner()) {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter('initiativeOwner', [fullName], ...commonParams)();
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleDuesoon30FilterMyInitiatives = useCallback(() => {
        const commonParams = [
            'initiative',        // activeFilterType
            'Table',             // menuKey
            'Table View',         // menuLabel
            <TableOutlined />,   // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'initiativeCount'
        ];
        const pastDueKey = 'Planning summary simplified';
        const pastDueValues = ['Due soon In 30 days'];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention']
        if (isInitiativeOwner()) {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter('initiativeOwner', [fullName], ...commonParams)();
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleDuesoonFilterMyInitiatives = useCallback(() => {
        const commonParams = [
            'initiative',        // activeFilterType
            'Table',             // menuKey
            'Table View',         // menuLabel
            <TableOutlined />,   // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'initiativeCount'
        ];
        const pastDueKey = 'Planning summary simplified';
        const pastDueValues = ['Due soon'];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention']
        if (isInitiativeOwner()) {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter('initiativeOwner', [fullName], ...commonParams)();
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            applyFilter(pastDueKey, pastDueValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleDuesoonFor30MyMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];

        // Apply initiative filters (stage and weekly status)
        applyFilter(
            'stage',
            ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();

        // Apply milestone filters (planning summary and status) - NO OWNER FILTER
        applyFilter(
            'Planning summary (simplified)',
            ['Due soon for 30 days'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();
        applyFilter(
            'Milestone Status',
            ['Completed', 'Started', 'Not Started'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();

        // IMPORTANT: No Owner filter applied here
        // This shows ALL late milestones from initiatives the user has access to
        // NOT just the ones where they are the milestone owner

    }, [applyFilter, colorByAttribute, selectedGroup]);
    const handleDuesoonMyMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];

        // Apply initiative filters (stage and weekly status)
        applyFilter(
            'stage',
            ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();

        // Apply milestone filters (planning summary and status) - NO OWNER FILTER
        applyFilter(
            'Planning summary (simplified)',
            ['Due soon'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();
        applyFilter(
            'Milestone Status',
            ['Completed', 'Started', 'Not Started'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();
    }, [applyFilter, colorByAttribute, selectedGroup]);
    const handleFilterMissingMyInitiatives = useCallback(() => {
        const commonParams = [
            'initiative',        // activeFilterType
            'Table',             // menuKey
            'Table View',         // menuLabel
            <TableOutlined />,   // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'initiativeCount'
        ];
        const actualImpactFilterKey = 'Actual impact update check (for last completed and previous period)';
        const actualImpactFilterValues = [
            'Missing actual in previous months',
            'Missing actual in last completed month',
            'Missing actual in last completed and previous completed months'
        ];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention'];
        if (isInitiativeOwner()) {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter('initiativeOwner', [fullName], ...commonParams)();
            applyFilter(actualImpactFilterKey, actualImpactFilterValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            applyFilter(actualImpactFilterKey, actualImpactFilterValues, ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);

    const handleLFourPlusInitiatives = useCallback(() => {
        const commonParams = [
            'initiative',      // activeFilterType
            'Table',           // menuKey
            'Table View',      // menuLabel
            <TableOutlined />, // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'netRecurringBenefitsPlanned',
        ];

        const stageValues = ['L4', 'Submitted for L5 Approval', 'L5'];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention'];
        const initiativeOwnerInfo = 'initiativeOwnerInfo' || 'workstreamLeadInfo' || 'workstreamSponsorInfo';
        if (!isInitiativeOwner()) {
            applyFilter('stage', stageValues, ...commonParams)();
            applyFilter('segmentalPBT', [true], ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            applyFilter('stage', stageValues, ...commonParams)();
            applyFilter('segmentalPBT', [true], ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleRecurringImpact = useCallback(() => {
        const commonParams = [
            'initiative',      // activeFilterType
            'Table',           // menuKey
            'Table View',      // menuLabel
            <TableOutlined />, // menuIcon
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'netRecurringBenefitsPlanned'
        ];
        const stageValue = ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'];
        const pastDueWeeklyKey = 'weeklyStatus';
        const pastDueWeeklyValue = ['On Track', 'Some Issue', 'Leadership Attention'];
        if (!isInitiativeOwner()) {
            applyFilter('stage', stageValue, ...commonParams)();
            applyFilter('segmentalPBT', [true], ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        } else {
            // Then apply the main recurring impact filter
            applyFilter('stage', stageValue, ...commonParams)();
            applyFilter('segmentalPBT', [true], ...commonParams)();
            applyFilter(pastDueWeeklyKey, pastDueWeeklyValue, ...commonParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, selectedGroup]);

    const handleOverDueMilestoneClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'Planning summary simplified',
                ['Past due'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,

                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,

                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon

                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
            applyFilter(
                'Planning summary simplified',
                ['Past due'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,

                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser])
    const handleMyDashboardClick = useCallback(() => {
        const currentDate = moment(); // Use moment directly
        const dateRangeValue = {
            fromDate: currentDate.clone().subtract(30, 'days'),
            toDate: currentDate.clone(),
        };

        const filtersToApply = [];

        if (isInitiativeOwner) {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            filtersToApply.push({
                filterKey: 'initiativeOwner',
                value: [fullName],
                type: 'initiative',
            });
        }
        filtersToApply.push(
            {
                filterKey: 'Planned value last update date (calendarized impact)',
                value: [dateRangeValue],
                type: 'impact',
            },
            {
                filterKey: 'Actual value last update date (calendarized impact)',
                value: [dateRangeValue],
                type: 'impact',
            }
        );
        filtersToApply.forEach(({ filterKey, value, type }) => {
            applyFilter(
                filterKey,
                value,
                type,
                'List', // menuKey for initiatives
                'List View', // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null
            )();
        });
    }, [applyFilter, isInitiativeOwner, currentUser, colorByAttribute, selectedGroup]);
    const handleOverDueInitiativeClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'Planning summary simplified',
                ['Due soon'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
            applyFilter(
                'Planning summary simplified',
                ['Due soon'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                'initiativeCount',
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser])
    const handlePipelineOverviewClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Waterfall',
                'Waterfall Chart',
                <FileTextOutlined />,
                'workstreamSponsorWorkstream',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Number of Initiatives',
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Waterfall',
                'Waterfall Chart',
                <FileTextOutlined />,
                'workstreamSponsorWorkstream',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Number of Initiatives',
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Waterfall',           // menuKey for initiatives
                'Waterfall Chart',
                <FileTextOutlined />,
                'workstreamSponsorWorkstream',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Number of Initiatives',
            )();
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Waterfall',
                'Waterfall Chart',
                <FileTextOutlined />,
                'workstreamSponsorWorkstream',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Number of Initiatives',
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Waterfall',
                'Waterfall Chart',
                <FileTextOutlined />,
                'workstreamSponsorWorkstream',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Number of Initiatives',
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleNetRecurringBenefitStackedClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Hold', 'On Track', 'Some Issue', 'Leadership Attention', 'Cancelled'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'Net Recurring Benefits (Planned, by stage, ETB Mn)',
                'netRecurringBenefitsPlanned'
            )();
        }

    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleImpactOverviewClick = useCallback(() => {
        const aggegationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'TableView',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'segmentalPBT', // The filterKey for milestone planning summary
                [true],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'TableView',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute
            )()
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'TableView',       // menuLabel
                <TableOutlined />,  // menuIcon
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'TableView',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'segmentalPBT', // The filterKey for milestone planning summary
                [true],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'TableView',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'TableView',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                null,
                null,
                null,
                'initiatives',
                aggegationAttribute,
            )();
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute
            )()
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleYTDL3PlusClick = useCallback(() => {
        // 1. Calculate the dynamic YTD date range
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1); // January 1st of the current year
        const ytdDateRange = [startOfYear, today];

        const aggregationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];
        const baseArgs = {
            activeFilterType: 'initiative',
            menuKey: 'Table',
            menuLabel: 'Table View',
            menuIcon: <TableOutlined />,
            colorByAttribute: 'workstreamSponsorWorkstream',
            selectedGroup: 'month',
            selectedInfoMenu: 'initiatives',
            sortField: null,
            sortOrder: null,
            yAxis: null,
            aggregationKey: aggregationAttribute,
            dateRange: ytdDateRange, // <-- Now dynamic!
        };

        if (!isInitiativeOwner()) {
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

            applyFilter(
                'initiativeOwner',
                [fullName],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                baseArgs.activeFilterType,
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'frequencyAndDirection',
                ['Recurring Benefit (ETB)', 'Recurring Negative Impact (ETB)'],
                'impact',
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOnetimeBenefitClick = useCallback(() => {
        const aggegationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'segmentalPBT', // The filterKey for milestone planning summary
                [true],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'frequencyAndDirection', // The filterKey for milestone planning summary
                ['One Time Benefit'],            // The values for the milestone planning summary filter
                'impact',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'segmentalPBT', // The filterKey for milestone planning summary
                [true],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'frequencyAndDirection', // The filterKey for milestone planning summary
                ['One Time Benefit'],            // The values for the milestone planning summary filter
                'impact',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOnetimeBenefitYTDClick = useCallback(() => {
        // 1. Calculate the dynamic YTD date range
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const ytdDateRange = [startOfYear, today];

        const aggregationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];

        // 2. Define a base set of arguments for clarity and reusability
        const baseArgs = {
            menuKey: 'Table',
            menuLabel: 'Table View',
            menuIcon: <TableOutlined />,
            colorByAttribute: 'workstreamSponsorWorkstream',
            selectedGroup: 'month',
            selectedInfoMenu: 'initiatives',
            sortField: null,
            sortOrder: null,
            yAxis: null,
            aggregationKey: aggregationAttribute,
            dateRange: ytdDateRange, // Now dynamic!
        };

        if (!isInitiativeOwner()) {
            // Apply multiple filters for non-initiative owners
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative', // activeFilterType for 'weeklyStatus' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'frequencyAndDirection',
                ['One Time Benefit'],
                'impact', // Correct activeFilterType for 'frequencyAndDirection' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        } else {
            // Apply multiple filters for initiative owners
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

            applyFilter(
                'initiativeOwner',
                [fullName],
                'initiative', // activeFilterType for 'initiativeOwner' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative', // activeFilterType for 'weeklyStatus' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'frequencyAndDirection',
                ['One Time Benefit'],
                'impact', // Correct activeFilterType for 'frequencyAndDirection' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOnetimeImplementationCostClick = useCallback(() => {
        const aggegationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'UniversalKPI', // The filterKey for milestone planning summary
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],            // The values for the milestone planning summary filter
                'kpi',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],         // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'UniversalKPI', // The filterKey for milestone planning summary
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],             // The values for the milestone planning summary filter
                'kpi',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'year',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOnetimeImplementationCostYTDClick = useCallback(() => {
        // 1. Calculate the dynamic YTD date range
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const ytdDateRange = [startOfYear, today];

        const aggregationAttribute = ['plannedImpactTotal', 'actualImpactTotal'];

        // 2. Define a base set of arguments for clarity and reusability
        const baseArgs = {
            menuKey: 'Table',
            menuLabel: 'Table View',
            menuIcon: <TableOutlined />,
            colorByAttribute: 'workstreamSponsorWorkstream',
            selectedGroup: 'month',
            selectedInfoMenu: 'initiatives',
            sortField: null,
            sortOrder: null,
            yAxis: null,
            aggregationKey: aggregationAttribute,
            dateRange: ytdDateRange, // Now dynamic!
        };

        if (!isInitiativeOwner()) {
            // Apply multiple filters for non-initiative owners
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative', // activeFilterType for 'weeklyStatus' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'UniversalKPI',
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],
                'impact', // Corrected activeFilterType for costs
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        } else {
            // Apply multiple filters for initiative owners
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

            applyFilter(
                'initiativeOwner',
                [fullName],
                'initiative', // activeFilterType for 'initiativeOwner' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative', // activeFilterType for 'stage' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative', // activeFilterType for 'weeklyStatus' filter
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();

            applyFilter(
                'UniversalKPI',
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],
                'impact', // Corrected activeFilterType for costs
                baseArgs.menuKey,
                baseArgs.menuLabel,
                baseArgs.menuIcon,
                baseArgs.colorByAttribute,
                baseArgs.selectedGroup,
                baseArgs.selectedInfoMenu,
                baseArgs.sortField,
                baseArgs.sortOrder,
                baseArgs.yAxis,
                baseArgs.aggregationKey,
                baseArgs.dateRange
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMissingActualClick = useCallback(() => {
        const aggegationAttribute = ['initiativeCount'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L3', 'L4', 'L5'],         // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Actual impact update check (for last completed and previous period)', // ✅ Add comma here
                ['Missing actual in last completed month', 'Missing actual in previous months', 'Missing actual in last completed and previous completed months'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Actual impact update check (for last completed and previous period)', // ✅ Add comma here
                ['Missing actual in last completed month', 'Missing actual in previous months', 'Missing actual in last completed and previous completed months'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualImpactUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMissingKPIActualClick = useCallback(() => {
        const aggegationAttribute = ['initiativeCount'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L3', 'L4', 'L5'],         // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Actual KPI update check (for last completed and previous period)', // ✅ Add comma here
                ['Missing actual in last completed month', 'Missing actual in previous months', 'Missing actual in last completed and previous completed months'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Actual KPI update check (for last completed and previous period)', // ✅ Add comma here
                ['Missing actual in last completed month', 'Missing actual in previous months', 'Missing actual in last completed and previous completed months'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                'workstreamSponsorWorkstream',
                'actualKpiUpdateCheck',
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser])
    const handleOverdueInitiativeClickCTO = useCallback(() => {
        const aggegationAttribute = ['initiativeCount', 'netRecurringBenefitsPlanned'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],          // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Past due'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Past due'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOverdueMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'Planning summary (simplified)',
                ['Late', 'Late to start'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Late', 'Late to start'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);


    const handleLatelyStartedMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'Planning summary (simplified)',
                ['Lately Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Lately Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);

    const handleLatelyCompletedMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'Planning summary (simplified)',
                ['Lately Completed'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Lately Completed'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOverdueMyMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];

        // Apply initiative filters (stage and weekly status)
        applyFilter(
            'stage',
            ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
        )();
        applyFilter(
            'Planning summary (simplified)',
            ['Late', 'Late to start'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();
        applyFilter(
            'Milestone Status',
            ['Completed', 'Started', 'Not Started'],
            'milestone',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'milestones',
            null,
            null,
            null,
            aggegationAttribute,
        )();

    }, [applyFilter, colorByAttribute, selectedGroup]);
    const handleDueSoonInitiativeClickCTO = useCallback(() => {
        const aggegationAttribute = ['initiativeCount', 'netRecurringBenefitsPlanned'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Due soon'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Due soon'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleDueSoonNext30DaysInitiativeClickCTO = useCallback(() => {
        const aggegationAttribute = ['initiativeCount', 'netRecurringBenefitsPlanned'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Due soon In 30 days'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary simplified', // ✅ Add comma here
                ['Due soon In 30 days'],
                'initiative',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleDueSoonMilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Due soon'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Due soon'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleDueSoonFor30MilestoneClickCTO = useCallback(() => {
        const aggegationAttribute = ['totalMilestone', 'totalMilestoneOwners'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Due soon for 30 days', 'Due soon'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'Planning summary (simplified)', // ✅ Add comma here
                ['Due soon for 30 days', 'Due soon'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            applyFilter(
                'Milestone Status',
                ['Completed', 'Started', 'Not Started'],
                'milestone',
                'Table',
                'Table View',
                <TableOutlined />,
                colorByAttribute,
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleDueSoonInitiativeClick = useCallback(() => {

    });
    const handleDueSoonMilestoneClick = useCallback(() => {

    });
    const handleIntiativeCountClick = useCallback(() => {
        const aggegationAttribute = ['initiativeCount'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'initiativeOwnerInfo',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'initiativeOwnerInfo',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'initiativeOwnerInfo',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'initiativeOwnerInfo',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'initiativeOwnerInfo',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMilestoneCountClick = useCallback(() => {
        const aggegationAttribute = ['totalMilestone'];
        if (!isInitiativeOwner()) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],           // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'Owner',
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'Owner',
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Table',           // menuKey for initiatives
                'Table View',       // menuLabel
                <TableOutlined />,  // menuIcon
                'Owner',
                selectedGroup,
                'initiatives',
                null,
                null,
                null,
                aggegationAttribute,
            )();
            // Then apply late milestones filter
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'Owner',
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )(); // Immediately invoke the inner function returned by applyFilter
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',                     // The activeFilterType for milestone filters
                'Table',                         // menuKey for milestones
                'Table View',                     // menuLabel (can be specific to this second part)
                <TableOutlined />,
                'Owner',
                selectedGroup,
                'milestones',
                null,
                null,
                null,
                aggegationAttribute,
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleDependancyClick = useCallback((clickedSponsor, clickedCrossFunctionalWorkstream) => {
        const allSubworkstreamNames = workstream.map((work) => work?.subworkstreamName).filter(Boolean);
        const aggregationAttribute = ['initiativeCount'];
        const commonStaticFilterArgs = [
            'initiative', // activeFilterType
            'Table',      // menuKey
            'Table View',  // menuLabel
            <TableOutlined />, // menuIcon
            'crossFunctionalSupportWorkstream', // This is your `colorByAttribute` from props
            'workstreamSponsorWorkstream', // This is 'workstreamSponsorWorkstream' in your Dependency component
            'initiatives', // filteredCategory
            null,
            null,
            null,
            aggregationAttribute,
        ];
        if (!isInitiativeOwner()) {
            // If a specific cross-functional workstream was clicked (from table row)
            if (clickedCrossFunctionalWorkstream) {
                applyFilter(
                    'crossFunctionalSupportWorkstreamInfo', // Filter by specific cross-functional support
                    [clickedCrossFunctionalWorkstream],
                    ...commonStaticFilterArgs
                )();
                // If you also want to filter by the sponsor in this case:
                if (clickedSponsor && clickedSponsor !== 'Unknown Sponsor') {
                    applyFilter(
                        'workstreamSponsorWorkstream', // Filter by specific sponsor
                        [clickedSponsor],
                        ...commonStaticFilterArgs
                    )();
                }

            } else if (clickedSponsor) {
                // If only the card (workstream sponsor) was clicked (not a specific row)
                applyFilter(
                    'workstreamSponsorWorkstream', // Filter by specific workstream sponsor
                    [clickedSponsor],
                    ...commonStaticFilterArgs
                )();
            }

            // Apply general filters that should *always* be active for non-owners,
            // assuming `applyFilter` accumulates or you want them to override.
            // If these should apply *in addition* to the specific clicked filter,
            // your `applyFilter` or filter state needs to support multiple active filters.
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )();
            // If you always want to filter by ALL cross-functional workstream names for non-owners,
            // irrespective of the specific card/row clicked:
            applyFilter(
                'crossFunctionalSupportWorkstreamInfo',
                allSubworkstreamNames, // Use all unique names
                ...commonStaticFilterArgs
            )();


        } else {
            // Logic for Initiative Owner
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

            // Apply filters based on what was clicked, for the owner
            if (clickedCrossFunctionalWorkstream) {
                applyFilter(
                    'crossFunctionalSupportWorkstreamInfo',
                    [clickedCrossFunctionalWorkstream],
                    ...commonStaticFilterArgs
                )();
                // Always apply owner filter when a specific item is clicked
                applyFilter(
                    'initiativeOwner', // Assuming this is the correct filter key for owner
                    [fullName],
                    ...commonStaticFilterArgs
                )();
                // If you also want to filter by the sponsor in this case:
                if (clickedSponsor && clickedSponsor !== 'Unknown Sponsor') {
                    applyFilter(
                        'workstreamSponsorWorkstream', // Filter by specific sponsor
                        [clickedSponsor],
                        ...commonStaticFilterArgs
                    )();
                }
            } else if (clickedSponsor) {
                // Only sponsor clicked
                applyFilter(
                    'workstreamSponsorWorkstream',
                    [clickedSponsor],
                    ...commonStaticFilterArgs
                )();
                // Always apply owner filter when a specific item is clicked
                applyFilter(
                    'initiativeOwner',
                    [fullName],
                    ...commonStaticFilterArgs
                )();
            } else {
                // Default: If no specific dependency clicked, just filter by owner and general conditions
                applyFilter(
                    'initiativeOwner',
                    [fullName],
                    ...commonStaticFilterArgs
                )();
            }

            // Apply other general filters that should *always* be active for owners.
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )();
            // If you always want to filter by ALL cross-functional workstream names for owners:
            applyFilter(
                'crossFunctionalSupportWorkstreamInfo',
                allSubworkstreamNames, // Use all unique names
                ...commonStaticFilterArgs
            )();
        }
    }, [
        applyFilter,
        isInitiativeOwner,
        currentUser,
        workstream, // Add workstream to dependencies
        colorByAttribute // Add colorByAttribute to dependencies
    ]);
    const handleChangeLogClick = useCallback((initiativeNumbersToFilter) => {
        if (!isInitiativeOwner) {
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],          // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'initiativeNumber', // The filterKey for milestone planning summary
                initiativeNumbersToFilter,            // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,  // menuIcon
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'stage', // The filterKey for milestone planning summary
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],          // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'weeklyStatus', // The filterKey for milestone planning summary
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],            // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
            applyFilter(
                'initiativeNumber', // The filterKey for milestone planning summary
                initiativeNumbersToFilter,            // The values for the milestone planning summary filter
                'initiative',
                'List',           // menuKey for initiatives
                'List View',       // menuLabel
                <UnorderedListOutlined />,
                colorByAttribute,
                'stage',
                'initiatives',
                null,
                null,
                null,
            )();
        }
    }, [
        applyFilter,
        isInitiativeOwner,
        currentUser,
    ]);
    const handleTopInitiativesClick = useCallback(() => {
        const baseParams = [
            'initiative',
            'List',
            'List View',
            <UnorderedListOutlined />,
            'weeklyStatus',
            'workstreamSponsorWorkstream',
            'initiatives',
            'netRecurringBenefitsLatest',
            'desc',
            null
        ];

        if (!isInitiativeOwner) {
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...baseParams
            )();

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...baseParams
            )();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

            applyFilter(
                'initiativeOwner',
                [fullName],
                ...baseParams
            )();

            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...baseParams
            )();

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...baseParams
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleCardClick = useCallback((clickedSponsor, clickedCrossFunctionalWorkstream, filterType) => {

        const aggregationAttribute = ['initiativeCount', 'netRecurringBenefitsPlanned', 'initiativeOwnerCount', 'initiativesPerOwner', 'implementationCostsPlanned'];

        // Common arguments for applyFilter calls
        const commonStaticFilterArgs = [
            'initiative', // activeFilterType
            filterType === 'statistics' ? 'Table' : 'Stacked', // menuKey
            filterType === 'statistics' ? 'Table View' : 'Stacked Chart', // menuLabel
            filterType === 'statistics' ? <TableOutlined /> : <BarChartOutlined />, // menuIcon
            'Stage', // colorByAttribute
            filterType === 'statistics' ? 'workstreamSponsorWorkstream' : selectedGroup, // selectedGroup (assuming this is the intent)
            'initiatives', // selectedInfoMenu (assuming this is the intent)
            null, // sortField (explicitly null if not needed)
            null, // sortOrder (explicitly null if not needed)
            filterType === 'statistics' ? null : filterType === 'count' ? 'Number of Initiatives' : 'Net Recurring Benefit',
            filterType === 'statistics' ? aggregationAttribute : null, // aggregationKey
        ];

        if (!isInitiativeOwner()) {
            // Logic for non-initiative owners
            // For these cards, clickedCrossFunctionalWorkstream is null, so it falls into clickedSponsor logic
            applyFilter(
                'workstreamSponsorWorkstream',
                [clickedSponsor],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply general filters that should *always* be active for non-owners
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately
        } else {
            // Logic for Initiative Owner
            const fullName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown Owner';

            // Always apply owner filter when a card is clicked for an owner
            applyFilter(
                'initiativeOwner',
                [fullName],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply sponsor filter if a specific sponsor card was clicked
            // This will overwrite any previous 'workstreamSponsorWorkstream' filter applied by allWorkstreamSponsorNames
            // if both were intended to be active concurrently, you'd need a different filtering mechanism.
            applyFilter(
                'workstreamSponsorWorkstream',
                [clickedSponsor],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply other general filters that should *always* be active for owners.
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately
        }
    }, [
        applyFilter,
        isInitiativeOwner,
        currentUser,
        subworkstream // Added subworkstream to dependencies as it's used to derive allWorkstreamSponsorNames
    ]);
    const handleStageBasedClick = useCallback((clickedSponsor, clickedCrossFunctionalWorkstream, filterType) => {
        // Common arguments for applyFilter calls
        const commonStaticFilterArgs = [
            'initiative', // activeFilterType
            'Waterfall', // menuKey
            'Waterfall Chart', // menuLabel
            <FileTextOutlined />, // menuIcon
            'Stage', // colorByAttribute
            selectedGroup, // selectedGroup (assuming this is the intent)
            'initiatives', // selectedInfoMenu (assuming this is the intent)
            null, // sortField (explicitly null if not needed)
            null, // sortOrder (explicitly null if not needed)
            filterType === 'Net Recurring Benefit' ? 'Net Recurring Benefit' : 'Number of Initiatives',
        ];
        if (!isInitiativeOwner()) {
            // Logic for non-initiative owners
            // For these cards, clickedCrossFunctionalWorkstream is null, so it falls into clickedSponsor logic
            applyFilter(
                'workstreamSponsorWorkstream',
                [clickedSponsor],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply general filters that should *always* be active for non-owners
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately
        } else {
            // Logic for Initiative Owner
            const fullName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown Owner';

            // Always apply owner filter when a card is clicked for an owner
            applyFilter(
                'initiativeOwner',
                [fullName],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply sponsor filter if a specific sponsor card was clicked
            // This will overwrite any previous 'workstreamSponsorWorkstream' filter applied by allWorkstreamSponsorNames
            // if both were intended to be active concurrently, you'd need a different filtering mechanism.
            applyFilter(
                'workstreamSponsorWorkstream',
                [clickedSponsor],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            // Apply other general filters that should *always* be active for owners.
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately

            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                ...commonStaticFilterArgs
            )(); // <--- Call the returned function immediately
        }
    }, [
        applyFilter,
        isInitiativeOwner,
        currentUser,
        subworkstream // Added subworkstream to dependencies as it's used to derive allWorkstreamSponsorNames
    ]);
    const handleMyInitiativesAtGlace = useCallback(() => {
        const baseParams = [
            'initiative',          // activeFilterType
            'List',                // menuKey
            'List View',           // menuLabel
            <UnorderedListOutlined />, // menuIcon
            'planningSummary',     // metric
            selectedGroup,
            'initiatives',
            'Stage',               // sortField
            'asc',                 // sortOrder
            null
        ];

        if (!isInitiativeOwner()) { // call the function, not just the reference
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                ...baseParams
            )();
            applyFilter('weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'], ...baseParams)();
        } else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter(
                'initiativeOwner',
                [fullName],
                ...baseParams
            )();
            applyFilter('weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'], ...baseParams)();
        }
    }, [applyFilter, isInitiativeOwner, currentUser, selectedGroup]);
    const handleMilestonesForMyInitiative = useCallback(() => {
        const baseParams = [
            'milestone',
            'List',
            'List View',
            <UnorderedListOutlined />,
            'planningSummary',
            'initiativeName',
            'milestones',
            'Milestone completion date',
            'asc',
            null
        ];
        const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
        if (!isInitiativeOwner) {
            applyFilter(
                'Milestone Status',
                ['Started', 'Not Started', 'Completed'],
                ...baseParams
            )();
            applyFilter(
                'Owner',
                [fullName],
                ...baseParams
            )();
        } else {
            applyFilter(
                'Milestone Status',
                ['Started', 'Not Started', 'Completed'],
                ...baseParams
            )();
            applyFilter(
                'Owner',
                [fullName],
                ...baseParams
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMyMilestonesClick = useCallback(() => {
        const baseParams = [
            'milestone',
            'List',
            'List View',
            <UnorderedListOutlined />,
            'planningSummary',
            selectedGroup,
            'milestones',
            'Milestone completion date',
            'asc',
            null
        ];
        const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
        if (!isOtherUser) {

            applyFilter(
                'Owner',
                [fullName],
                ...baseParams
            )();
            applyFilter(
                'Milestone Status',
                ['Started', 'Not Started', 'Completed'],
                ...baseParams
            )();
        } else {
            applyFilter(
                'Owner',
                [fullName],
                ...baseParams
            )();
            applyFilter(
                'Milestone Status',
                ['Started', 'Not Started', 'Completed'],
                ...baseParams
            )();
        }
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleTOPipelineOverviewClick = useCallback((filterType) => {
        const commonStatic = [
            'initiative',
            'Waterfall',
            'Waterfall Chart',
            <FileTextOutlined />,
            'workstreamSponsorWorkstream',
            selectedGroup,
            'initiatives',
            null,
            null,
            filterType === 'Net Recurring Benefit' ? 'Net Recurring Benefit' : 'Number of Initiatives',
        ]
        applyFilter(
            'stage',
            ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
            ...commonStatic
        )();
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            ...commonStatic
        )();

    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleClickAgenda = useCallback(() => {
        const commonStatic = [
            null,
            'Note',
            'Note',
            <FileDoneOutlined />,
            colorByAttribute,
            selectedGroup,
            null,
            null,
            null,
            null,
        ]
        applyFilter(
            null,
            [],
            ...commonStatic
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleL1PlusClick = useCallback(() => {
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'totalL1PlusNetRecurringBenefit'
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleL2PlusClick = useCallback(() => {
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'totalL2PlusNetRecurringBenefit'
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleL3PlusClick = useCallback(() => {
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'totalL3PlusNetRecurringBenefit'
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleSelectedInitiatives = useCallback(() => {
        const commonStaticFilter = [
            'initiative',
            'List',
            'List View',
            <UnorderedListOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            null
        ]
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            ...commonStaticFilter
        )();
        applyFilter(
            'selectedForToMeetingDescusion',
            [true],
            ...commonStaticFilter
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handlePendingApprovalClick = useCallback(() => {
        const commonStaticFilter = [
            'initiative',
            'List',
            'List View',
            <UnorderedListOutlined />,
            colorByAttribute,
            'Stage (simplified)',
            'initiatives',
            'Stage (simplified)',
            'asc',
            null,
            null
        ]
        applyFilter(
            'weeklyStatus',
            ['On Track', 'Some Issue', 'Leadership Attention', 'On track'],
            ...commonStaticFilter
        )();
        applyFilter(
            'stageSimplified',
            ['Submitted for L1 Approval', 'Submitted for L2 Approval', 'Submitted for L3 Approval', 'Submitted for L4 Approval', 'Submitted for L5 Approval'],
            ...commonStaticFilter
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const workstreamActionClick = useCallback((workstreams) => {
        const common = [
            'workstreamAction',
            'List',
            'List View',
            <UnorderedListOutlined />,
            colorByAttribute,
            selectedGroup,
            'workstreamactions',
            null,
            null,
            null
        ];
        applyFilter(
            'status',
            ['Not Completed'],
            ...common
        )();
        // Handle workstream filtering
        applyFilter(
            'Accountable Workstream',
            Array.isArray(workstreams) ? workstreams : [workstreams].filter(Boolean),
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleTypeRowClick = useCallback((type) => {
        const aggregationAttribute = ['plannedLoanKPI', 'plannedFinancingKPI', 'plannedFCYKPI', 'actualDepositKPI', 'actualLoanKPI', 'actualFinancingKPI', 'actualFCYKPI', 'actualAccount', 'plannedAccount'];
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'year',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'TypeOfKPI',
            [type],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'year',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleKpiRowClick = useCallback((universalKPI, type) => {
        const aggregationAttribute = universalKPI === 'Account Opening' ? ['plannedAccount', 'actualAccount'] : [`planned${universalKPI}KPI`, `actual${universalKPI}KPI`];
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'year',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'TypeOfKPI',
            [type],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'year',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
        applyFilter(
            'UniversalKPI',
            [universalKPI],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'year',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMonthlyTypeClick = useCallback((type) => {
        const aggregationAttribute = ['plannedLoanKPI', 'plannedFinancingKPI', 'plannedFCYKPI', 'actualDepositKPI', 'actualLoanKPI', 'actualFinancingKPI', 'actualFCYKPI', 'actualAccount', 'plannedAccount'];
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'month',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'TypeOfKPI',
            [type],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'month',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleMonthlyKpiGroupClick = useCallback((universalKPI, type) => {
        const aggregationAttribute = universalKPI === 'Account Opening' ? ['plannedAccount', 'actualAccount'] : [`planned${universalKPI}KPI`, `actual${universalKPI}KPI`];
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'month',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'TypeOfKPI',
            [type],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'month',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
        applyFilter(
            'UniversalKPI',
            [universalKPI],
            'kpi',
            'Table',
            'Table View',
            <TableOutlined />,
            'workstreamSponsorWorkstream',
            'month',
            'initiatives',
            null,
            null,
            null,
            aggregationAttribute
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);

    const handlePending = useCallback((filterValue) => {
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'initiativeCount'
        ];
        applyFilter(
            'stageSimplified',
            [filterValue],
            ...common
        )();
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleActionItemClick = useCallback((actionItemNumber) => {
        const common = [
            'workstreamAction',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'workstreamactions',
            null,
            null,
            null,
            'workstreamActionCount'
        ];
        applyFilter(
            'actionItemNumber',
            Array.isArray(actionItemNumber) ? actionItemNumber : [actionItemNumber].filter(Boolean),
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOneTimeBenefitStackedClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'stage',
                ['L0', 'L1', , 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage (simplified)',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'segmentalPBT',
                [true],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage (simplified)',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Hold', 'On Track', 'Some Issue', 'Leadership Attention', 'Cancelled'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'frequencyAndDirection', // The filterKey for milestone planning summary
                ['One Time Benefit'],            // The values for the milestone planning summary filter
                'impact',                     // The activeFilterType for milestone filters
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            // Apply initiative owner filter
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage (simplified)',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'stage',
                ['L0', 'L1', , 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
            applyFilter(
                'frequencyAndDirection', // The filterKey for milestone planning summary
                ['One Time Benefit'],            // The values for the milestone planning summary filter
                'impact',                     // The activeFilterType for milestone filters
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)',
                'oneTimeBenefitsPlanned'
            )();
        }

    }, [applyFilter, isInitiativeOwner, currentUser]);
    const handleOneTimeImplementationCostStackedClick = useCallback(() => {
        if (!isInitiativeOwner) {
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Hold', 'On Track', 'Some Issue', 'Leadership Attention', 'Cancelled'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
            applyFilter(
                'UniversalKPI', // The filterKey for milestone planning summary
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],            // The values for the milestone planning summary filter
                'kpi',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
        }
        else {
            const fullName = `${currentUser.firstName} ${currentUser.lastName}`;
            applyFilter(
                'initiativeOwner', // The filterKey for initiative owner
                [fullName],        // The value for the initiative owner filter
                'initiative',      // The activeFilterType for initiative filters
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
            applyFilter(
                'stage',
                ['L0', 'L1', 'L2', 'L3', 'L4', 'L5'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
            applyFilter(
                'weeklyStatus',
                ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
                'initiative',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
            applyFilter(
                'UniversalKPI', // The filterKey for milestone planning summary
                ['One Time Implementation Cost for Amortization', 'One Time Implementation Cost for Depreciation'],            // The values for the milestone planning summary filter
                'kpi',
                'Stacked',
                'Stacked Chart',
                <BarChartOutlined />,
                'Stage',
                selectedGroup,
                'initiatives',
                null,
                null,
                'One Time Implementation Costs (Planned, ETB Mn)',
                'implementationCostsPlanned'
            )();
        }

    }, [applyFilter, isInitiativeOwner, currentUser]);
    const LWClick = useCallback(() => {
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            '(LW)L1+netrecurringbenefits'
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'stage',
            ['L1', 'L2', 'L3', 'L4', 'L5'],
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const currentClick = useCallback(() => {
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'totalL1PlusNetRecurringBenefit'
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'stage',
            ['L1', 'L2', 'L3', 'L4', 'L5'],
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    const changeClick = useCallback(() => {
        const common = [
            'initiative',
            'Table',
            'Table View',
            <TableOutlined />,
            colorByAttribute,
            selectedGroup,
            'initiatives',
            null,
            null,
            null,
            'totalL1PlusNetRecurringBenefit'
        ];
        applyFilter(
            'weeklyStatus',
            ['On Track', 'On track', 'Some Issue', 'Leadership Attention'],
            ...common
        )();
        applyFilter(
            'stage',
            ['L1', 'L2', 'L3', 'L4', 'L5'],
            ...common
        )();
        applyFilter(
            'Pipeline weekly variance (L1+)',
            ['Increased value', 'New Initiative', 'Decreased value', 'Cancelled/On hold', 'Unchanged'],
            ...common
        )();
    }, [applyFilter, isInitiativeOwner, currentUser]);
    return {
        handleFilterMyInitiativesAndLateMilestones,
        handleFilterMissingMyInitiatives,
        handleFilterMyInitiatives,
        handleLFourPlusInitiatives,
        handleRecurringImpact,
        handleOverDueInitiativeClick,
        handleOverDueMilestoneClick,
        handleDueSoonInitiativeClick,
        handleDueSoonMilestoneClick,
        handleMyDashboardClick,
        handlePipelineOverviewClick,
        handleNetRecurringBenefitStackedClick,
        handleImpactOverviewClick,
        handleYTDL3PlusClick,
        handleOnetimeBenefitClick,
        handleOnetimeBenefitYTDClick,
        handleOnetimeImplementationCostClick,
        handleOnetimeImplementationCostYTDClick,
        handleMissingActualClick,
        handleOverdueInitiativeClickCTO,
        handleOverdueMilestoneClickCTO,
        handleDueSoonInitiativeClickCTO,
        handleDueSoonMilestoneClickCTO,
        handleIntiativeCountClick,
        handleMilestoneCountClick,
        handleDependancyClick,
        handleChangeLogClick,
        handleTopInitiativesClick,
        handleCardClick,
        handleStageBasedClick,
        handleMyInitiativesAtGlace,
        handleMilestonesForMyInitiative,
        handleMyMilestonesClick,
        handleTOPipelineOverviewClick,
        handleClickAgenda,
        handleL1PlusClick,
        handleL2PlusClick,
        handleL3PlusClick,
        handleSelectedInitiatives,
        handlePendingApprovalClick,
        workstreamActionClick,
        handlePending,
        handleActionItemClick,
        handleOneTimeBenefitStackedClick,
        handleOneTimeImplementationCostStackedClick,
        LWClick,
        currentClick,
        changeClick,
        handleDueSoonNext30DaysInitiativeClickCTO,
        handleDueSoonFor30MilestoneClickCTO,
        handleTypeRowClick,
        handleKpiRowClick,
        handleMonthlyTypeClick,
        handleMonthlyKpiGroupClick,
        handleMissingKPIActualClick,
        handleOverdueMyMilestoneClickCTO,
        handleLatelyCompletedMilestoneClickCTO,
        handleLatelyStartedMilestoneClickCTO,
        handleDuesoonMyMilestoneClickCTO,
        handleDuesoonFilterMyInitiatives,
        handleDuesoon30FilterMyInitiatives,
        handleDuesoonFor30MyMilestoneClickCTO
    };
};